import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArtifactViewer } from '../components/artifacts/ArtifactViewer';
import { ArtifactVersionHistory } from '../components/artifacts/ArtifactVersionHistory';
import { ArtifactExport } from '../components/artifacts/ArtifactExport';
import { ArtifactDependencyGraph } from '../components/artifacts/ArtifactDependencyGraph';
import { LineageViewer } from '../components/artifacts/LineageViewer';
import * as artifactService from '../services/artifactService';
import { useTheme } from '../contexts/ThemeContext';
import { FiBox, FiChevronRight } from 'react-icons/fi';

// Type badge colour map
const typeBadgeClasses: Record<string, { dark: string; light: string }> = {
  text:             { dark: 'bg-blue-900/40 text-blue-300',       light: 'bg-blue-100 text-blue-700' },
  code:             { dark: 'bg-green-900/40 text-green-300',     light: 'bg-green-100 text-green-700' },
  image:            { dark: 'bg-pink-900/40 text-pink-300',       light: 'bg-pink-100 text-pink-700' },
  idea:             { dark: 'bg-yellow-900/40 text-yellow-300',   light: 'bg-yellow-100 text-yellow-700' },
  critique:         { dark: 'bg-red-900/40 text-red-300',         light: 'bg-red-100 text-red-700' },
  solution:         { dark: 'bg-indigo-900/40 text-indigo-300',   light: 'bg-indigo-100 text-indigo-700' },
  document:         { dark: 'bg-gray-700/60 text-gray-300',       light: 'bg-gray-100 text-gray-700' },
  diagram:          { dark: 'bg-teal-900/40 text-teal-300',       light: 'bg-teal-100 text-teal-700' },
  decision_log:     { dark: 'bg-amber-900/40 text-amber-300',     light: 'bg-amber-100 text-amber-700' },
  pulse_digest:     { dark: 'bg-cyan-900/40 text-cyan-300',       light: 'bg-cyan-100 text-cyan-700' },
  handoff_note:     { dark: 'bg-orange-900/40 text-orange-300',   light: 'bg-orange-100 text-orange-700' },
  mindmap:          { dark: 'bg-violet-900/40 text-violet-300',   light: 'bg-violet-100 text-violet-700' },
  project_manifest: { dark: 'bg-emerald-900/40 text-emerald-300', light: 'bg-emerald-100 text-emerald-700' },
};

const getTypeBadge = (type: string, darkMode: boolean) => {
  const classes = typeBadgeClasses[type] ?? { dark: 'bg-gray-700/60 text-gray-300', light: 'bg-gray-100 text-gray-700' };
  return darkMode ? classes.dark : classes.light;
};

export const ArtifactDetail: React.FC = () => {
  const { artifactId } = useParams<{ artifactId: string }>();
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  const [activeTab, setActiveTab] = useState<'content' | 'dependencies' | 'lineage' | 'history'>('content');
  const [lineageViewMode, setLineageViewMode] = useState<'full' | 'ancestry' | 'descendants'>('full');

  const { data: artifact, isLoading, error } = useQuery(
    ['artifact', artifactId],
    () => artifactId ? artifactService.getArtifact(artifactId) : Promise.reject('No artifact ID'),
    { enabled: !!artifactId }
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500" />
      </div>
    );
  }

  if (error || !artifact) {
    return (
      <div className={`mx-auto max-w-lg mt-10 p-6 rounded-lg border text-center ${
        darkMode ? 'bg-red-900/20 border-red-700 text-red-400' : 'bg-red-50 border-red-200 text-red-600'
      }`}>
        Error loading artifact
      </div>
    );
  }

  const handleNodeClick = (clickedArtifactId: string) => {
    if (clickedArtifactId !== artifactId) {
      navigate(`/artifacts/${clickedArtifactId}`);
    }
  };

  // Tab config
  const tabs = [
    { id: 'content',      label: 'Content',      activeClasses: 'border-blue-500 text-blue-400' },
    { id: 'dependencies', label: 'Dependencies',  activeClasses: 'border-purple-500 text-purple-400' },
    { id: 'lineage',      label: 'Lineage',       activeClasses: 'border-teal-500 text-teal-400' },
    { id: 'history',      label: 'History',       activeClasses: 'border-amber-500 text-amber-400' },
  ] as const;

  return (
    <div>
      {/* Breadcrumb */}
      <nav className="flex items-center space-x-1 mb-6 text-sm">
        <Link
          to="/artifacts"
          className={`flex items-center hover:underline ${darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}
        >
          <FiBox className="mr-1 h-4 w-4" />
          Artifacts
        </Link>
        <FiChevronRight className={`h-4 w-4 ${darkMode ? 'text-gray-600' : 'text-gray-400'}`} />
        <span className={`truncate max-w-xs ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
          {artifact.name}
        </span>
      </nav>

      {/* Header card */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4 relative">
        <style>{`
          .artifact-header-card::before {
            content: '';
            position: absolute;
            top: -10px; left: -10px; right: -10px; bottom: -10px;
            background: ${darkMode ? 'rgba(15, 23, 42, 0.7)' : 'rgba(255, 255, 255, 0.7)'};
            border-radius: 12px;
            z-index: 0;
            box-shadow: 0 8px 32px rgba(0,0,0,0.1), 0 4px 8px rgba(59,130,246,0.2);
            backdrop-filter: blur(8px);
            animation: artifactPulse 4s infinite;
          }
          @keyframes artifactPulse {
            0%, 100% { box-shadow: 0 8px 32px rgba(0,0,0,0.1), 0 4px 8px rgba(59,130,246,0.2); }
            50%       { box-shadow: 0 8px 32px rgba(0,0,0,0.15), 0 4px 12px rgba(59,130,246,0.3); }
          }
        `}</style>

        <div className="artifact-header-card w-full flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          {/* Left: icon + name + badges */}
          <div className="relative z-10">
            <h1 className="text-3xl font-bold flex items-center">
              <div className={`mr-3 p-2 rounded-lg ${
                darkMode
                  ? 'bg-gradient-to-br from-blue-900 to-purple-900 shadow-inner shadow-blue-700/30'
                  : 'bg-gradient-to-br from-blue-100 to-purple-100 shadow-inner shadow-blue-300/30'
              }`}>
                <FiBox className={`h-6 w-6 ${darkMode ? 'text-blue-300' : 'text-blue-700'}`} />
              </div>
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-500 to-purple-600 drop-shadow-sm">
                {artifact.name}
              </span>
            </h1>

            {/* Badges row */}
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {/* Type badge */}
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getTypeBadge(artifact.type, darkMode)}`}>
                {artifact.type}
              </span>
              {/* Version badge */}
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'
              }`}>
                v{artifact.version || '1'}
              </span>
              {/* Created by */}
              {(artifact.agent_name || artifact.created_by || artifact.persona_id) && (
                <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  by {artifact.agent_name || artifact.created_by || artifact.persona_id}
                </span>
              )}
              {/* Phase badge */}
              {artifact.phase && (
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                  darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'
                }`}>
                  {artifact.phase}
                </span>
              )}
            </div>
          </div>

          {/* Right: export button */}
          <div className="relative z-10 flex items-center space-x-2">
            <ArtifactExport artifact={artifact} />
          </div>
        </div>
      </div>

      {/* Tab bar */}
      <div className={`mb-6 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
        <nav className={`flex space-x-1 px-1 rounded-t-lg ${darkMode ? 'bg-gray-900/50' : ''}`}>
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`py-4 px-4 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.id
                  ? tab.activeClasses
                  : darkMode
                    ? 'border-transparent text-gray-400 hover:text-gray-200 hover:border-gray-500'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab content */}
      <div>
        {/* Content tab */}
        {activeTab === 'content' && (
          <ArtifactViewer artifact={artifact} />
        )}

        {/* Dependencies tab */}
        {activeTab === 'dependencies' && (
          <div className={`rounded-lg border p-4 ${
            darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
          }`}>
            <ArtifactDependencyGraph
              artifactId={artifact.id}
              height="600px"
              onNodeClick={handleNodeClick}
            />
            <p className={`mt-4 text-sm text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Visualizing artifact dependencies and relationships. Click on any node to navigate to that artifact.
            </p>
          </div>
        )}

        {/* Lineage tab */}
        {activeTab === 'lineage' && (
          <div className={`rounded-lg border p-4 ${
            darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
          }`}>
            {/* Lineage view mode toggle */}
            <div className="mb-4 flex justify-end">
              <div className={`inline-flex rounded-lg border overflow-hidden ${
                darkMode ? 'border-gray-600' : 'border-gray-300'
              }`} role="group">
                {(['ancestry', 'full', 'descendants'] as const).map((mode, idx, arr) => (
                  <button
                    key={mode}
                    type="button"
                    className={`px-4 py-2 text-sm font-medium transition-colors ${
                      idx === 0 ? '' : `border-l ${darkMode ? 'border-gray-600' : 'border-gray-300'}`
                    } ${
                      lineageViewMode === mode
                        ? 'bg-blue-600 text-white'
                        : darkMode
                          ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                          : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                    onClick={() => setLineageViewMode(mode)}
                  >
                    {mode === 'full' ? 'Full Lineage' : mode.charAt(0).toUpperCase() + mode.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            <LineageViewer
              artifactId={artifact.id}
              height="600px"
              onNodeClick={handleNodeClick}
              viewMode={lineageViewMode}
            />
          </div>
        )}

        {/* History tab */}
        {activeTab === 'history' && (
          <div className={`rounded-lg border p-4 ${
            darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
          }`}>
            <ArtifactVersionHistory
              artifactId={artifact.id}
              artifactType={artifact.type}
            />
          </div>
        )}
      </div>
    </div>
  );
};
